// src/components/documents/DocumentPreview.jsx - V2 ENTERPRISE PORTAL
// ═══════════════════════════════════════════════════════════════════════════
// 👁️ DOCUMENT PREVIEW — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 V2: Portal a document.body — stacking context independiente
// 🔥 V2: Dark mode automático
// 🔥 ULTRA ROBUSTO:    Soporte para imágenes, PDFs y documentos sin preview
// 🔥 ULTRA RÁPIDO:     GPU-accelerated + lazy iframe para PDFs
// 🔥 ULTRA EFICIENTE:  React.memo + loading state + error handling
// 🔥 ULTRA PROFESIONAL: Barra de metadatos + botón download + responsive
// ═══════════════════════════════════════════════════════════════════════════

import React, { memo, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Download,
  Loader2,
  FileText,
  Image,
  File,
  Database,
  Calendar,
  HardDrive,
} from 'lucide-react';
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from '../../utils/portalUtils';

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 CONSTANTES DE ANIMACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const MODAL_ANIMATION = {
  overlay: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  panel: {
    initial: { scale: 0.9, opacity: 0, y: 30 },
    animate: { scale: 1, opacity: 1, y: 0 },
    exit: { scale: 0.9, opacity: 0, y: 30 },
    transition: { type: 'spring', stiffness: 350, damping: 28, mass: 0.8 },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 SUB-COMPONENTE: METADATO INDIVIDUAL
// ═══════════════════════════════════════════════════════════════════════════

const MetaItem = memo(function MetaItem({ icon: Icon, label, value, isMobile }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon
        size={isMobile ? 12 : 14}
        className="text-slate-400 dark:text-slate-500 flex-shrink-0"
        strokeWidth={1.5}
      />
      <span className={`text-slate-600 dark:text-slate-400 truncate ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
        {value || '—'}
      </span>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 👁️ COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const DocumentPreview = memo(function DocumentPreview({
  doc,
  isOpen,
  onClose,
  onDownload,
  isMobile,
  primaryColor = '#2563eb',
  formatFileSize,
  getFileTypeInfo,
}) {
  // ─── Hooks ──────────────────────────────────────────────────────────
  const mounted = usePortal();

  // ✅ Portal hooks
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);

  // ─── Derivados ───────────────────────────────────────────────────────
  const fileInfo = useMemo(
    () => getFileTypeInfo?.(doc?.fileType) || { icon: 'File', color: 'text-slate-500', label: 'FILE' },
    [doc?.fileType, getFileTypeInfo]
  );

  const formattedDate = useMemo(() => {
    if (!doc?.createdAt) return null;
    try {
      return new Date(doc.createdAt).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  }, [doc?.createdAt]);

  const formattedSize = useMemo(
    () => formatFileSize?.(doc?.size) || '0 KB',
    [doc?.size, formatFileSize]
  );

  const isImage = doc?.fileType?.startsWith('image/');
  const isPdf = doc?.fileType === 'application/pdf';
  const isCloud = doc?.storage === 'indexeddb' || doc?.hasLargeFile;
  const isLoading = doc?.loading;

  // ─── Handlers ────────────────────────────────────────────────────────
  const handleDownload = useCallback(() => {
    onDownload?.(doc);
  }, [doc, onDownload]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose?.();
      }
    },
    [onClose]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════

  if (!isOpen || !doc) return null;

  // 🌓 Clases dark mode
  const bgModal = "bg-white dark:bg-slate-900";
  const borderLight = "border-slate-100 dark:border-slate-700";
  const textDark = "text-slate-900 dark:text-slate-100";
  const textGray = "text-slate-500 dark:text-slate-400";
  const bgPreview = "bg-slate-50 dark:bg-slate-800/50";
  const bgBadgeCloud = "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30";
  const bgBadgeType = "text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800";
  const bgNoPreview = "bg-slate-200 dark:bg-slate-700";
  const bgNoContent = "bg-amber-100 dark:bg-amber-900/30";
  const textNoContent = "text-slate-600 dark:text-slate-400";

  const modalContent = (
    <AnimatePresence>
      <motion.div
        {...MODAL_ANIMATION.overlay}
        className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[99999] p-3"
        onClick={handleOverlayClick}
        aria-modal="true"
        role="dialog"
        aria-label={`Visualizar: ${doc?.title || 'Documento'}`}
      >
        <motion.div
          {...MODAL_ANIMATION.panel}
          className={`
            ${bgModal} rounded-2xl w-full max-w-4xl
            shadow-2xl border ${borderLight}
            max-h-[90vh] overflow-hidden flex flex-col
            ${isMobile ? 'p-4' : 'p-5 md:p-6'}
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ═══════════════════════════════════════════════════════════ */}
          {/* HEADER CON METADATOS                                        */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pb-3 border-b ${borderLight} flex-shrink-0`}>
            <div className="flex-1 min-w-0">
              <h2
                className={`font-bold ${textDark} truncate ${
                  isMobile ? 'text-base' : 'text-lg'
                }`}
                title={doc?.title}
              >
                {doc?.title || 'Sem título'}
              </h2>

              {/* Metadatos */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                <span className={`${textGray} ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                  {doc?.fileName}
                </span>

                {formattedDate && (
                  <MetaItem
                    icon={Calendar}
                    value={formattedDate}
                    isMobile={isMobile}
                  />
                )}

                <MetaItem
                  icon={HardDrive}
                  value={formattedSize}
                  isMobile={isMobile}
                />

                {isCloud && (
                  <span className={`inline-flex items-center gap-1 ${bgBadgeCloud} px-2 py-0.5 rounded-full text-[9px] font-medium`}>
                    <Database size={10} strokeWidth={1.5} />
                    Cloud
                  </span>
                )}

                <span className={`inline-flex items-center gap-1 ${bgBadgeType} px-2 py-0.5 rounded-full text-[9px] font-medium`}>
                  {fileInfo.label}
                </span>
              </div>
            </div>

            {/* Botón cerrar */}
            <button
              onClick={onClose}
              className="p-2 -m-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors active:scale-95 self-end sm:self-center flex-shrink-0"
              aria-label="Fechar preview"
            >
              <X size={isMobile ? 18 : 20} className="text-slate-400 dark:text-slate-500" strokeWidth={1.5} />
            </button>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* CONTENIDO DEL PREVIEW                                       */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div
            className={`
              flex-1 flex items-center justify-center ${bgPreview} rounded-xl my-3
              ${isMobile ? 'min-h-[250px] p-2' : 'min-h-[400px] p-4'}
            `}
          >
            {/* Loading state */}
            {isLoading && (
              <div className="flex flex-col items-center gap-3">
                <Loader2
                  className={`animate-spin ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}
                  style={{ color: primaryColor }}
                />
                <p className={`text-slate-500 dark:text-slate-400 font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  Carregando documento...
                </p>
              </div>
            )}

            {/* Imagen */}
            {!isLoading && isImage && doc?.base64 && (
              <motion.img
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                src={doc.base64}
                alt={doc?.title || 'Preview'}
                className="max-w-full max-h-[300px] md:max-h-[500px] object-contain rounded-lg shadow-sm"
                draggable={false}
              />
            )}

            {/* PDF */}
            {!isLoading && isPdf && doc?.base64 && (
              <motion.iframe
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                src={doc.base64}
                className={`w-full rounded-lg border-0 ${
                  isMobile ? 'h-[300px]' : 'h-[500px]'
                }`}
                title={doc?.title || 'PDF Preview'}
                sandbox="allow-scripts allow-same-origin"
              />
            )}

            {/* Sin preview disponible */}
            {!isLoading && !isImage && !isPdf && (
              <div className="flex flex-col items-center gap-3 text-center px-4">
                <div
                  className={`inline-flex items-center justify-center rounded-2xl ${bgNoPreview} ${
                    isMobile ? 'w-12 h-12' : 'w-16 h-16'
                  }`}
                >
                  <File
                    size={isMobile ? 24 : 32}
                    className="text-slate-400 dark:text-slate-500"
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <p className={`font-medium text-slate-700 dark:text-slate-300 ${isMobile ? 'text-sm' : 'text-base'}`}>
                    Pré-visualização não disponível
                  </p>
                  <p className={`text-slate-400 dark:text-slate-500 mt-1 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                    Faça o download do arquivo para visualizar
                  </p>
                </div>
              </div>
            )}

            {/* Sin contenido */}
            {!isLoading && !doc?.base64 && (isImage || isPdf) && (
              <div className="flex flex-col items-center gap-3 text-center px-4">
                <div
                  className={`inline-flex items-center justify-center rounded-2xl ${bgNoContent} ${
                    isMobile ? 'w-12 h-12' : 'w-16 h-16'
                  }`}
                >
                  <FileText
                    size={isMobile ? 24 : 32}
                    className="text-amber-500 dark:text-amber-400"
                    strokeWidth={1.5}
                  />
                </div>
                <p className={`${textNoContent} ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  Conteúdo do arquivo não disponível
                </p>
              </div>
            )}
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* FOOTER CON BOTÓN DOWNLOAD                                   */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className={`flex justify-end pt-3 border-t ${borderLight} flex-shrink-0`}>
            <button
              onClick={handleDownload}
              className={`
                inline-flex items-center gap-2
                text-white font-semibold rounded-xl
                transition-all duration-200 active:scale-95
                shadow-md
                ${isMobile ? 'px-5 py-2.5 text-sm' : 'px-5 py-2.5 text-sm'}
              `}
              style={{
                backgroundColor: primaryColor,
              }}
            >
              <Download size={isMobile ? 16 : 18} strokeWidth={2} />
              Download
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return portalize(mounted, modalContent);
});

// ═══════════════════════════════════════════════════════════════════════════
// 📤 EXPORT
// ═══════════════════════════════════════════════════════════════════════════

DocumentPreview.displayName = 'DocumentPreview';
export default DocumentPreview;